/** Default page size for paginated admin data tables. */
export const DEFAULT_PAGE_SIZE = 50;
/** Full dump size for dropdowns, reference lists, matrices, and exports. */
export const ALL_DATA_LIMIT = 10000;

/** Normalize list API envelope; also tolerates legacy bare arrays. */
export function normalizeListResponse(
  payload,
  fallbackPage = 1,
  fallbackLimit = DEFAULT_PAGE_SIZE,
) {
  if (Array.isArray(payload)) {
    return {
      data: payload,
      page: fallbackPage,
      limit: fallbackLimit,
      total: payload.length,
      totalPages: payload.length === 0 ? 0 : 1,
    };
  }

  const data = Array.isArray(payload?.data) ? payload.data : [];
  const page = Number(payload?.page) || fallbackPage;
  const limit = Number(payload?.limit) || fallbackLimit;
  const total = Number.isFinite(Number(payload?.total))
    ? Number(payload.total)
    : data.length;
  const totalPages = Number.isFinite(Number(payload?.totalPages))
    ? Number(payload.totalPages)
    : total === 0
      ? 0
      : Math.max(1, Math.ceil(total / limit));

  return { data, page, limit, total, totalPages };
}

/**
 * Build POST /list body for a paged data-table request.
 * Defaults to page size 50 — only use this for screens with pagination controls.
 * Also accepts an already-built wire body ({ page, limit, export, filters }).
 */
export function listRequestBody(options = {}) {
  // Pass-through / normalize when callers pass dropdownListBody()/exportListBody() output.
  if (
    typeof options.export === "boolean" &&
    options.filters &&
    typeof options.filters === "object" &&
    !Array.isArray(options.filters) &&
    options.exportMode === undefined
  ) {
    const body = {
      page: Number(options.page) > 0 ? Number(options.page) : 1,
      limit:
        Number(options.limit) > 0 ? Number(options.limit) : DEFAULT_PAGE_SIZE,
      export: options.export,
      filters: { ...options.filters },
    };
    if (options.search != null && String(options.search).trim() !== "") {
      body.search = String(options.search).trim();
    }
    return body;
  }

  const {
    page = 1,
    limit = DEFAULT_PAGE_SIZE,
    search,
    exportMode = false,
    filters = {},
  } = options;

  const body = {
    page,
    limit: exportMode ? ALL_DATA_LIMIT : limit,
    export: Boolean(exportMode),
    filters: {},
  };
  if (search != null && String(search).trim() !== "") {
    body.search = String(search).trim();
  }
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "string" && value.trim() === "") return;
    body.filters[key] = value;
  });
  return body;
}

/**
 * Body for dropdown / reference / non-table lists.
 * Loads up to ALL_DATA_LIMIT (10000) so the UI gets the full catalog.
 */
export function dropdownListBody(extraFilters = {}) {
  return listRequestBody({
    page: 1,
    limit: ALL_DATA_LIMIT,
    exportMode: true,
    filters: extraFilters,
  });
}

/**
 * Body for exports / matrices / config screens that need a full capped dump.
 */
export function exportListBody(extraFilters = {}) {
  return listRequestBody({
    page: 1,
    exportMode: true,
    filters: extraFilters,
  });
}
